import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";

import { injectAgentStore } from "../../../../../agent-ui";
import { PRINCIPAL_SERVICE } from "../../../../../viewer-components/services/principal.token";
import { DebugStreamService } from "../../../services/debug-stream.service";
import { buildChatTraceReport } from "./chat-trace-report";
import type { ChatTraceReport } from "./chat-trace-report";

/**
 * Exclusive "Report chat trace" dialog: shows the live trace identity (date,
 * user, agent/thread/run ids, session runs, initial input, conversation text)
 * in a readable form plus a free-form feedback field, and copies the whole
 * bundle as one JSON document to the clipboard for the support team.
 *
 * All state comes from the root DebugStreamService signals (registered by the
 * chat container) and the agent store — nothing is passed through dialog data,
 * mirroring how AgentDebugDialogComponent reads its live sources.
 */
@Component({
  selector: "app-chat-report-dialog",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: "./chat-report-dialog.component.html",
  styleUrl: "./chat-report-dialog.component.scss",
})
export class ChatReportDialogComponent {
  readonly #debugStream = inject(DebugStreamService);

  readonly #principalService = inject(PRINCIPAL_SERVICE, { optional: true });

  /** Live store of the registered agent — the conversation source (restored threads included). */
  readonly #store = injectAgentStore(computed(() => this.#debugStream.traceAgentId() ?? undefined));

  /** Frozen at open time — the report describes what the user saw when reporting. */
  readonly #generatedAt = new Date();

  /** Download filename — date-based, unique per dialog open (millisecond precision), colon-free for filesystem safety. */
  protected readonly reportFileName = `chat-trace-report_${this.#generatedAt.toISOString().replace(/:/g, "-")}.json`;

  protected readonly feedback = signal("");

  protected readonly copied = signal(false);

  /** Full report including the live feedback — recomputed as the user types. */
  protected readonly report = computed<ChatTraceReport>(() =>
    buildChatTraceReport({
      generatedAt: this.#generatedAt,
      user: this.#principalService?.getPrincipal() ?? null,
      agentId: this.#debugStream.traceAgentId(),
      threadId: this.#debugStream.traceThreadId(),
      runs: this.#debugStream.runTrace(),
      rawMessages: this.#store()?.messages() ?? [],
      feedback: this.feedback(),
    }),
  );

  protected onFeedbackInput(event: Event): void {
    this.feedback.set((event.target as HTMLTextAreaElement).value);
  }

  /** Copies a single trace field (thread/run id) for quick sharing. */
  protected copyValue(value: string | null): void {
    if (!value) return;
    void navigator.clipboard?.writeText(value).catch(() => undefined);
  }

  /** Copies the complete report JSON — feedback included — to the clipboard. */
  protected async copyReport(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(this.report(), null, 2));
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      /* clipboard not available */
    }
  }

  /**
   * Downloads the report JSON with a date-based unique filename so it can be
   * attached to a ticket or workflow. Blob URL instead of a data URL — long
   * conversations can exceed browser data-URL size limits.
   */
  protected downloadReport(): void {
    const blob = new Blob([JSON.stringify(this.report(), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = this.reportFileName;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }
}
